/**
 * Fleet integrity, polled.
 *
 * Deliberately not on the telemetry socket: these are trend judgements the
 * backend recomputes about once a second, and pushing them at frame rate would
 * put a re-render on the hot path to say the same thing sixty times.
 *
 * One store, polled once, read by every surface that shows a badge — the fleet
 * row, the track panel and the caution banner must never disagree about whether
 * an aircraft's position is trustworthy.
 */

import { useSyncExternalStore } from 'react';

import { getIntegrity, type IntegrityFinding, type IntegrityReport } from '@/api/integrity';

const POLL_MS = 2000;

let report: IntegrityReport = { drones: {}, checked: 0 };
const listeners = new Set<() => void>();
let timer: number | null = null;
let subscribers = 0;

const EMPTY: IntegrityFinding[] = [];

function emit(): void {
  for (const listener of listeners) listener();
}

async function poll(): Promise<void> {
  const result = await getIntegrity();
  if (!result.ok) return;
  // Reference equality is what stops a re-render per poll on a healthy fleet,
  // which is the common case.
  if (JSON.stringify(result.data) === JSON.stringify(report)) return;
  report = result.data;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  subscribers += 1;
  if (timer === null) {
    void poll();
    timer = window.setInterval(() => void poll(), POLL_MS);
  }
  return () => {
    listeners.delete(listener);
    subscribers -= 1;
    // Stop polling when nothing is watching — the login screen should not be
    // asking about fleet integrity every two seconds.
    if (subscribers <= 0 && timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  };
}

/** Findings for one aircraft. Stable empty array so this is render-safe. */
export function useIntegrity(droneId: string | null): IntegrityFinding[] {
  return useSyncExternalStore(
    subscribe,
    () => (droneId ? (report.drones[droneId]?.findings ?? EMPTY) : EMPTY),
    () => EMPTY,
  );
}

/** 'critical' | 'caution' | null for one aircraft. */
export function useIntegritySeverity(droneId: string | null): string | null {
  return useSyncExternalStore(
    subscribe,
    () => (droneId ? (report.drones[droneId]?.severity ?? null) : null),
    () => null,
  );
}

/** Every aircraft currently flagged, for the fleet-wide banner. */
export function useFlaggedDrones(): Array<{ droneId: string; severity: string | null; findings: IntegrityFinding[] }> {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => report,
    () => ({ drones: {}, checked: 0 }) as IntegrityReport,
  );
  return Object.entries(snapshot.drones).map(([droneId, value]) => ({
    droneId,
    severity: value.severity,
    findings: value.findings,
  }));
}
